import qs from "qs";

import { parseBooleanValue, parseNumberValue } from "lib/hooks/useURLState";
import { ColumnSetting } from "lib/types/models";
import { Framework_Lots } from "../../lib/generated/app-service-gql/graphql";
import { FrameworkOrganisationCallOffs } from "../../lib/hooks/api/frameworks/useSearchFrameworkOrganisations";
import { SearchOrganisationsParams } from "../../lib/hooks/api/organisations/useOrganisations";
import {
  AnchorOrganisationType,
  OrgSortBy,
  OrgSortOrder,
  SearchOrgRole,
} from "../../lib/types/graphQLEnums";
import { convertSupplierSortToOrganisationSort } from "../../lib/utils/supplierUtils";

/**
 * Add any extra data required by columns that are available
 * on specific screens. The columns shouldn't render (or even throw an error)
 * if the data is not available.
 *
 * If the data will be available but is loading, pass an empty map.
 * When the data is ready, construct a map with supplier GUIDs as keys.
 */
export type AdditionalSupplierData = {
  frameworkData?: Map<
    string,
    {
      lots: Framework_Lots[];
      callOffStats: FrameworkOrganisationCallOffs | undefined;
      frameworkId: string;
    }
  >;
};

export type SortField = "name" | "country" | "sme" | "awards_count";

const validSortfields: SortField[] = ["name", "country", "sme", "awards_count"];

export function isValidSortField(field?: string): SortField | undefined {
  if (field && validSortfields.includes(field as SortField)) {
    return field as SortField;
  }
  return undefined;
}

export type SupplierFilters = {
  buyerIds?: string[];
  buyerCategories?: string[];
  buyerLists?: string[];
  suppliers?: number[];
  guids?: string[];
  text: string;
  isSme?: boolean | ""; // string allows for the "undefined" state to be set in the URL
  lotIds?: string[];
  sort?: {
    field: SortField;
    order: "ASC" | "DESC";
  };
};

export type SortState = Exclude<SupplierFilters["sort"], undefined>;

export type SupplierColumns =
  | "name"
  | "country"
  | "sme"
  | "notices"
  | "frameworkLots"
  | "frameworkLastActivity"
  | "frameworkCallOffs";

export const BASELINE_SUPPLIER_COLUMNS: ColumnSetting<SupplierColumns>[] = [
  { title: "Supplier", field: "name", disabled: true },
  { title: "Country", field: "country" },
  { title: "Awards count", field: "notices" },
  { title: "Is SME", field: "sme" },
];

const ALL_SUPPLIER_COLUMNS: ColumnSetting<SupplierColumns>[] = [
  ...BASELINE_SUPPLIER_COLUMNS,
  { title: "Lots", field: "frameworkLots" },
  { title: "Last activity", field: "frameworkLastActivity" },
  { title: "Call-offs", field: "frameworkCallOffs" },
];

export function convertSupplierFiltersToOrganisationParams(
  filters: SupplierFilters,
  pageSize: number,
  currentPage: number,
): SearchOrganisationsParams {
  return {
    limit: pageSize,
    page: currentPage,
    role: SearchOrgRole.Supplier,
    ids: filters.guids?.filter(
      (id) => !["SIGNALS:ALL_COMPETITORS", "SIGNALS:ALL_PARTNERS"].includes(id),
    ),
    names: filters.text ? [filters.text] : undefined,
    isSme: typeof filters.isSme === "boolean" ? filters.isSme : undefined,
    listIds: filters.buyerLists,
    pubSecOrgTypes: filters.buyerCategories,
    sortOrder: (filters.sort?.order as OrgSortOrder) || OrgSortOrder.Desc,
    sortBy:
      (filters.sort?.field && convertSupplierSortToOrganisationSort(filters.sort.field)) ||
      OrgSortBy.AwardCount,
    anchorOrganisation:
      filters.buyerIds?.length === 1
        ? { organisationId: filters.buyerIds[0], type: AnchorOrganisationType.Buyer }
        : undefined,
    include: { awardCount: true, noticeCount: false, aliases: true },
  };
}

export const DEFAULT_FILTERS: SupplierFilters = {
  text: "",
  sort: {
    field: "awards_count",
    order: "DESC",
  },
};

const isDefined = <T>(value: T | undefined): value is T => typeof value !== "undefined";

export function generateSupplierUrl(supplierFilters: Partial<SupplierFilters>, prefix = "") {
  const params = {
    supplierFilters,
  };
  const paramsString = qs.stringify(params, { arrayFormat: "brackets" });
  return `${prefix}?${paramsString}`;
}

export function parseSuppliersUrlState(
  state: unknown,
  defaultFilters: SupplierFilters,
): SupplierFilters {
  if (typeof state !== "object" || state === null) {
    return defaultFilters;
  }

  const filters = state as SupplierFilters;

  return {
    ...filters,
    isSme: parseBooleanValue(filters.isSme),
    suppliers: filters.suppliers?.map(parseNumberValue).filter(isDefined),
  };
}

const columnCopies: Record<SupplierColumns, string> = ALL_SUPPLIER_COLUMNS.reduce(
  (acc, { title, field }) => {
    if (field === "notices") {
      return { ...acc, awards_count: title };
    }

    return { ...acc, [field]: title };
  },
  {} as Record<SupplierColumns, string>,
);

export const getSupplierColumnCopy = (column?: string): string => {
  if (!column || !(column in columnCopies)) return "";
  return columnCopies[column as SupplierColumns];
};
